/**
 * ConvertQuoteModal — Quote-to-Order Frontend Completion.
 *
 * Small, focused modal for the admin "Convert to Order" flow. Reads
 * customer/delivery details from the operator, calls
 * POST /api/admin/quotes/{id}/convert-to-order, and on success shows
 * the created order id + an Open Order link.
 *
 * Reuses the same three delivery options and the same USPS-Stamp ack
 * gate that MissingStickers.jsx uses. Never touches pricing math —
 * amounts come exclusively from the quote / order snapshots.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { track, EVENT } from "@/lib/analytics";

const DELIVERY = [
  { code: "local_pickup", label: "Local Pickup", cost: 0 },
  { code: "usps_stamp",   label: "USPS Stamp",   cost: 4.00 },
  { code: "ups_ground",   label: "UPS Ground",   cost: 12.00 },
];

export default function ConvertQuoteModal({ quote, onClose, onConverted }) {
  const alreadyConverted = !!quote?.converted_order_public_id;
  const stored = quote?.customer || {};
  const [firstName, setFirstName] = useState(stored.first_name || "");
  const [lastName, setLastName] = useState(stored.last_name || "");
  const [email, setEmail] = useState(stored.email || "");
  const [phone, setPhone] = useState(stored.phone || "");
  const [delivery, setDelivery] = useState("local_pickup");
  const [addr, setAddr] = useState({ line1: "", line2: "", city: "", state: "FL", postal_code: "", country: "United States" });
  const [ack, setAck] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const needsAddr = delivery !== "local_pickup";
  const needsAck = delivery === "usps_stamp";

  if (alreadyConverted) {
    return (
      <ModalShell testid="convert-quote-modal-already" onClose={onClose}>
        <h3 className="font-display text-xl">This quote was already converted.</h3>
        <div className="mt-3 text-sm space-y-1">
          <div><span className="font-bold">Order:</span> <span className="font-mono" data-testid="already-converted-order-id">{quote.converted_order_public_id}</span></div>
          {quote.converted_at && (
            <div><span className="font-bold">Converted at:</span> {new Date(quote.converted_at).toLocaleString()}</div>
          )}
        </div>
        <footer className="mt-5 flex justify-end gap-2">
          <button className="btn-ghost text-sm" onClick={onClose} data-testid="convert-modal-close">Close</button>
          <Link
            to={`/admin/fulfillment/${quote.converted_order_public_id}`}
            className="btn-red text-sm no-underline inline-flex items-center gap-1"
            data-testid="convert-modal-open-existing-order"
          >Open Existing Order</Link>
        </footer>
      </ModalShell>
    );
  }

  if (result) {
    return (
      <ModalShell testid="convert-quote-modal-success" onClose={onClose}>
        <h3 className="font-display text-xl">Order created successfully</h3>
        <div className="mt-3 text-sm space-y-1">
          <div><span className="font-bold">Order:</span> <span className="font-mono" data-testid="convert-success-order-id">{result.order_public_id}</span></div>
          <div className="text-[var(--smoke)]">The order begins in <b>Awaiting Payment</b>. The quote is now <b>Converted</b> and linked to this order.</div>
        </div>
        <footer className="mt-5 flex justify-end gap-2">
          <button className="btn-ghost text-sm" onClick={onClose} data-testid="convert-modal-close">Close</button>
          <Link
            to={`/admin/fulfillment/${result.order_public_id}`}
            className="btn-red text-sm no-underline inline-flex items-center gap-1"
            data-testid="convert-modal-open-order"
          >Open Order</Link>
        </footer>
      </ModalShell>
    );
  }

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim()) {
      toast.error("First name, last name, email, and phone are all required.");
      return;
    }
    if (needsAddr) {
      for (const k of ["line1", "city", "state", "postal_code", "country"]) {
        if (!(addr[k] || "").trim()) {
          toast.error(`Shipping ${k} is required for delivery orders.`);
          return;
        }
      }
    }
    if (needsAck && !ack) {
      toast.error("USPS Stamp requires the no-tracking risk acknowledgment.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        customer: {
          first_name: firstName.trim(), last_name: lastName.trim(),
          email: email.trim(), phone: phone.trim(),
          ...(needsAddr ? { shipping_address: addr } : {}),
        },
        delivery: { code: delivery },
        usps_acknowledgment: needsAck ? ack : false,
        admin_note: note.trim() || null,
      };
      const r = await api.post(`/admin/quotes/${quote.public_id}/convert-to-order`, body);
      setResult(r.data);
      onConverted?.(r.data);
      // Fire ONLY on a real conversion — never on `already_converted`
      // (spec §3 forbids second fires). No quote/order id transmitted.
      if (!r.data?.already_converted) {
        track(EVENT.QUOTE_CONVERTED_TO_ORDER, {
          conversion_actor: "admin",
          delivery_method:  delivery,
          order_value:      Number(r.data?.order?.pricing_snapshot?.totals?.total
                                 || r.data?.pricing_snapshot?.totals?.total || 0),
          currency:         "USD",
          user_type:        "admin",
        }, { once: true, key: `q2o-${r.data?.order?.public_id || quote.public_id}` });
      }
      toast.success(r.data.already_converted ? "Quote was already converted." : "Order created.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed.");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell testid="convert-quote-modal" onClose={onClose}>
      <h3 className="font-display text-xl">Create Order from Quote {quote.public_id}</h3>
      <p className="text-xs text-[var(--smoke)] mt-1">One order will be created in <b>Awaiting Payment</b>. The quote will remain linked.</p>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <Input label="First name *" value={firstName} onChange={setFirstName} testid="cq-first" />
        <Input label="Last name *"  value={lastName}  onChange={setLastName}  testid="cq-last" />
        <Input label="Email *"      value={email}     onChange={setEmail}     testid="cq-email" type="email" />
        <Input label="Phone *"      value={phone}     onChange={setPhone}     testid="cq-phone" />
      </div>

      <fieldset className="mt-4">
        <legend className="text-[10px] uppercase tracking-widest font-bold">Delivery</legend>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
          {DELIVERY.map((d) => (
            <label key={d.code} className={`p-3 border-2 rounded-lg cursor-pointer ${delivery === d.code ? "border-[var(--panini-red)] bg-red-50" : "border-[var(--ink)] bg-white"}`} data-testid={`cq-delivery-${d.code}`}>
              <input type="radio" name="delivery" value={d.code} checked={delivery === d.code} onChange={() => setDelivery(d.code)} className="hidden" />
              <div className="font-bold text-sm">{d.label}</div>
              <div className="text-xs font-mono">{d.cost === 0 ? "Free" : `$${d.cost.toFixed(2)}`}</div>
            </label>
          ))}
        </div>
      </fieldset>

      {needsAddr && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="cq-address">
          <Input label="Line 1 *"     value={addr.line1}       onChange={(v) => setAddr({ ...addr, line1: v })}       testid="cq-addr-line1" />
          <Input label="Line 2"       value={addr.line2}       onChange={(v) => setAddr({ ...addr, line2: v })}       testid="cq-addr-line2" />
          <Input label="City *"       value={addr.city}        onChange={(v) => setAddr({ ...addr, city: v })}        testid="cq-addr-city" />
          <Input label="State *"      value={addr.state}       onChange={(v) => setAddr({ ...addr, state: v })}       testid="cq-addr-state" />
          <Input label="Postal code *" value={addr.postal_code} onChange={(v) => setAddr({ ...addr, postal_code: v })} testid="cq-addr-postal" />
          <Input label="Country *"    value={addr.country}     onChange={(v) => setAddr({ ...addr, country: v })}     testid="cq-addr-country" />
        </div>
      )}

      {needsAck && (
        <label className="mt-3 flex items-start gap-2 text-xs cursor-pointer p-3 border-2 border-[var(--panini-red)] bg-red-50 rounded-lg" data-testid="cq-usps-ack-panel">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--panini-red)]" data-testid="cq-usps-ack" />
          <span>Customer acknowledged USPS Stamp has no tracking and accepts the risk of loss or non-delivery.</span>
        </label>
      )}

      <Input label="Admin note (optional)" value={note} onChange={setNote} testid="cq-note" area />

      <footer className="mt-5 flex justify-between gap-2">
        <button className="btn-ghost text-sm" onClick={onClose} disabled={busy} data-testid="convert-modal-cancel">Cancel</button>
        <button className="btn-red text-sm" onClick={submit} disabled={busy} data-testid="convert-modal-submit">
          {busy ? "Creating…" : "Create Order from Quote"}
        </button>
      </footer>
    </ModalShell>
  );
}

function ModalShell({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" role="dialog" data-testid={testid}>
      <div className="w-full max-w-2xl bg-white border-2 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--ink)] p-5 my-8" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", area }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
      {area ? (
        <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" data-testid={testid} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm" data-testid={testid} />
      )}
    </label>
  );
}
